<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/logbook/testing/safety">Safety Tests</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit' : 'Create' }} Custom Test</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Custom Test' : 'Create Custom Test' }}</h1>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading test data...</p>
</div>

<!-- Form -->
<form *ngIf="!isLoading" [formGroup]="testForm" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column: Test Details & Questions -->
    <div class="col-lg-8">
      <!-- Basic Info Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Test Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <!-- Title -->
            <div class="col-12">
              <label class="form-label">Test Title <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="title"
                     placeholder="e.g., Company Equipment Safety Protocol">
              <div class="form-text">A descriptive title for your test</div>
            </div>

            <!-- Short Name & Icon -->
            <div class="col-md-6">
              <label class="form-label">Short Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="shortName"
                     placeholder="e.g., Equipment" maxlength="15">
              <div class="form-text">Used in badges and reports (max 15 chars)</div>
            </div>

            <div class="col-md-6">
              <label class="form-label">Icon</label>
              <div class="icon-selector">
                <div *ngFor="let icon of iconOptions" class="icon-option"
                     [class.selected]="testForm.get('icon')?.value === icon.value"
                     (click)="testForm.patchValue({ icon: icon.value })"
                     [title]="icon.label">
                  <i class="bi" [ngClass]="icon.value"></i>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="col-12">
              <label class="form-label">Description <span class="text-danger">*</span></label>
              <textarea appCharCounter class="form-control" formControlName="description" rows="3"
                        placeholder="Describe what this test covers and its purpose..."></textarea>
            </div>

            <!-- Settings Row -->
            <div class="col-md-4">
              <label class="form-label">Passing Score <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="passingScore"
                       min="1" max="100">
                <span class="input-group-text">%</span>
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label">Time Limit <span class="text-danger">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" formControlName="timeLimit"
                       min="5" max="180">
                <span class="input-group-text">minutes</span>
              </div>
            </div>

            <div class="col-md-4">
              <label class="form-label">&nbsp;</label>
              <div class="form-check mt-2">
                <input type="checkbox" class="form-check-input" formControlName="requiredAnnually" id="requiredAnnually">
                <label class="form-check-label" for="requiredAnnually">Required Annually</label>
              </div>
            </div>

            <!-- Additional Options -->
            <div class="col-12">
              <div class="d-flex gap-4">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" formControlName="shuffleQuestions" id="shuffleQuestions">
                  <label class="form-check-label" for="shuffleQuestions">Shuffle question order</label>
                </div>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" formControlName="showCorrectAnswers" id="showCorrectAnswers">
                  <label class="form-check-label" for="showCorrectAnswers">Show correct answers after completion</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Questions Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0"><i class="bi bi-list-ol me-2"></i>Questions</h5>
            <button type="button" class="btn btn-sm btn-primary" (click)="addQuestion()">
              <i class="bi bi-plus-lg me-1"></i>Add Question
            </button>
          </div>
        </div>
        <div class="card-body p-0">
          <!-- Questions List -->
          <div class="questions-list" formArrayName="questions">
            <div *ngFor="let question of questions.controls; let i = index; let first = first; let last = last"
                 class="question-item" [formGroupName]="i">
              <div class="question-header">
                <div class="d-flex align-items-center gap-2">
                  <span class="question-number">{{ i + 1 }}</span>
                  <span class="fw-medium">Question {{ i + 1 }}</span>
                </div>
                <div class="question-actions d-flex gap-1">
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveQuestion(i, 'up')"
                          [disabled]="first" title="Move up">
                    <i class="bi bi-chevron-up"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" (click)="moveQuestion(i, 'down')"
                          [disabled]="last" title="Move down">
                    <i class="bi bi-chevron-down"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" (click)="duplicateQuestion(i)"
                          title="Duplicate">
                    <i class="bi bi-copy"></i>
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost text-danger" (click)="removeQuestion(i)"
                          title="Remove">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </div>

              <div class="question-body">
                <!-- Question Text -->
                <div class="mb-3">
                  <label class="form-label">Question Text <span class="text-danger">*</span></label>
                  <textarea appCharCounter class="form-control" formControlName="text" rows="2"
                            placeholder="Enter your question..."></textarea>
                </div>

                <!-- Answer Options -->
                <div class="mb-3">
                  <label class="form-label d-flex justify-content-between align-items-center">
                    <span>Answer Options</span>
                    <button type="button" class="btn btn-sm btn-outline-secondary"
                            (click)="addOption(i)"
                            [disabled]="getQuestionOptions(i).length >= 6">
                      <i class="bi bi-plus-lg me-1"></i>Add Option
                    </button>
                  </label>
                  <div class="d-flex flex-column gap-2" formArrayName="options">
                    <div *ngFor="let option of getQuestionOptions(i).controls; let j = index"
                         class="option-item" [formGroupName]="j"
                         [class.correct-option]="option.get('isCorrect')?.value">
                      <div class="option-radio">
                        <input type="radio" class="form-check-input"
                               [name]="'correct_' + i"
                               [checked]="option.get('isCorrect')?.value"
                               (change)="setCorrectAnswer(i, j)">
                      </div>
                      <input type="text" class="form-control form-control-sm" formControlName="text"
                             [placeholder]="'Option ' + (j + 1)">
                      <button type="button" class="btn btn-sm btn-ghost text-danger"
                              (click)="removeOption(i, j)"
                              [disabled]="getQuestionOptions(i).length <= 2"
                              title="Remove option">
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                  <div class="form-text">
                    <i class="bi bi-info-circle me-1"></i>
                    Select the radio button next to the correct answer
                  </div>
                </div>

                <!-- Explanation (Optional) -->
                <div>
                  <label class="form-label">Explanation <span class="text-muted">(Optional)</span></label>
                  <textarea appCharCounter class="form-control form-control-sm" formControlName="explanation" rows="2"
                            placeholder="Explain why the correct answer is correct..."></textarea>
                  <div class="form-text">Shown to users after they complete the test</div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div *ngIf="questions.length === 0" class="text-center py-5">
              <i class="bi bi-question-circle text-muted fs-2-5rem"></i>
              <p class="text-muted mt-2 mb-3">No questions added yet</p>
              <button type="button" class="btn btn-primary btn-sm" (click)="addQuestion()">
                <i class="bi bi-plus-lg me-1"></i>Add First Question
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Summary & Actions -->
    <div class="col-lg-4">
      <!-- Preview Card -->
      <div class="card mb-4 preview-card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-eye me-2"></i>Preview</h5>
        </div>
        <div class="card-body">
          <div class="preview-icon mb-3">
            <i class="bi" [ngClass]="testForm.get('icon')?.value || 'bi-clipboard-check'"></i>
          </div>
          <h6 class="mb-1">{{ testForm.get('title')?.value || 'Test Title' }}</h6>
          <span class="badge badge-custom mb-2">Custom</span>
          <p class="small text-muted mb-3">
            {{ testForm.get('description')?.value || 'Test description will appear here...' }}
          </p>

          <div class="preview-stats">
            <div class="preview-stat">
              <span class="preview-stat-label">Questions</span>
              <span class="preview-stat-value">{{ questionCount }}</span>
            </div>
            <div class="preview-stat">
              <span class="preview-stat-label">Time Limit</span>
              <span class="preview-stat-value">{{ testForm.get('timeLimit')?.value }} min</span>
            </div>
            <div class="preview-stat">
              <span class="preview-stat-label">Passing</span>
              <span class="preview-stat-value">{{ testForm.get('passingScore')?.value }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-clipboard-data me-2"></i>Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-list">
            <div class="summary-item">
              <span class="summary-label">Total Questions</span>
              <span class="summary-value">{{ questionCount }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Time Limit</span>
              <span class="summary-value">{{ testForm.get('timeLimit')?.value }} minutes</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Passing Score</span>
              <span class="summary-value">{{ testForm.get('passingScore')?.value }}%</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Est. per Question</span>
              <span class="summary-value">{{ estimatedDuration }}</span>
            </div>
            <div class="summary-item" *ngIf="testForm.get('requiredAnnually')?.value">
              <span class="summary-label">Renewal</span>
              <span class="summary-value text-warning">Required Annually</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-lightbulb me-2"></i>Tips</h5>
        </div>
        <div class="card-body">
          <ul class="tips-list small">
            <li>Write clear, unambiguous questions</li>
            <li>Include 3-4 answer options per question</li>
            <li>Add explanations to help users learn</li>
            <li>Set a realistic time limit (1-2 min per question)</li>
            <li>Review questions before publishing</li>
          </ul>
        </div>
      </div>

      <!-- Actions Card -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-save me-2"></i>Actions</h5>
        </div>
        <div class="card-body">
          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving || testForm.invalid || questionCount === 0">
              <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isSaving" class="bi bi-check-lg me-2"></i>
              {{ isSaving ? 'Saving...' : (isEditMode ? 'Update Test' : 'Create Test') }}
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>

          <div class="alert alert-info mt-3 mb-0 small" *ngIf="questionCount === 0">
            <i class="bi bi-info-circle me-2"></i>
            Add at least one question to create the test.
          </div>
        </div>
      </div>
    </div>
  </div>
</form>
